import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import {
  ContactLeadStatus,
  IContactLead,
} from '../../../_interfaces/IContactLead';
import { ContactLeadsService } from '../../../_services/contact-leads/contact-leads.service';

type EditableContactLead = IContactLead & {
  _draftStatus: ContactLeadStatus;
  _draftNotes: string;
  _saving?: boolean;
};

@Component({
  selector: 'app-admin-contact-leads',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    TableModule,
    InputTextModule,
    ButtonModule,
  ],
  templateUrl: './admin-contact-leads.component.html',
  styleUrl: './admin-contact-leads.component.scss',
})
export class AdminContactLeadsComponent implements OnInit {
  loading = false;
  rows: EditableContactLead[] = [];
  total = 0;
  pending = 0;
  page = 0;
  size = 25;
  q = '';
  status: ContactLeadStatus | 'ALL' = 'ALL';

  readonly statusOptions: Array<{
    value: ContactLeadStatus;
    labelKey: string;
  }> = [
    { value: 'NEW', labelKey: 'ADMIN.CONTACT_LEADS.STATUSES.NEW' },
    { value: 'CONTACTED', labelKey: 'ADMIN.CONTACT_LEADS.STATUSES.CONTACTED' },
    { value: 'QUALIFIED', labelKey: 'ADMIN.CONTACT_LEADS.STATUSES.QUALIFIED' },
    { value: 'ARCHIVED', labelKey: 'ADMIN.CONTACT_LEADS.STATUSES.ARCHIVED' },
  ];

  constructor(
    private readonly contactLeadsService: ContactLeadsService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.loading = true;
    this.contactLeadsService
      .getAll({
        page: this.page,
        size: this.size,
        status: this.status,
        q: this.q,
      })
      .subscribe({
        next: (response) => {
          this.rows = (response?.items || []).map((row) => this.decorate(row));
          this.total = Number(response?.total || 0);
          this.pending = Number(response?.pending || 0);
          this.loading = false;
        },
        error: () => {
          this.rows = [];
          this.total = 0;
          this.pending = 0;
          this.loading = false;
          this.toast('error', 'ADMIN.CONTACT_LEADS.MSG.LOAD_ERROR');
        },
      });
  }

  applyFilters() {
    this.page = 0;
    this.load();
  }

  clearFilters() {
    this.q = '';
    this.status = 'ALL';
    this.applyFilters();
  }

  previousPage() {
    if (this.page <= 0) return;
    this.page -= 1;
    this.load();
  }

  nextPage() {
    if (!this.canGoNext) return;
    this.page += 1;
    this.load();
  }

  get canGoNext(): boolean {
    return (this.page + 1) * this.size < this.total;
  }

  save(row: EditableContactLead) {
    if (!row.id || row._saving) return;
    row._saving = true;
    this.contactLeadsService
      .update(row.id, {
        status: row._draftStatus,
        notes: row._draftNotes,
      })
      .subscribe({
        next: (updated) => {
          Object.assign(row, this.decorate(updated));
          row._saving = false;
          this.toast('success', 'ADMIN.CONTACT_LEADS.MSG.UPDATE_OK');
          this.load();
        },
        error: () => {
          row._saving = false;
          this.toast('error', 'ADMIN.CONTACT_LEADS.MSG.UPDATE_ERROR');
        },
      });
  }

  statusLabel(status: ContactLeadStatus | 'ALL'): string {
    if (status === 'ALL') {
      return this.translate.instant('ADMIN.CONTACT_LEADS.ALL_STATUSES');
    }
    return this.translate.instant(`ADMIN.CONTACT_LEADS.STATUSES.${status}`);
  }

  statusClass(status: ContactLeadStatus): string {
    return `vc-admin-contact-leads__status--${status.toLowerCase()}`;
  }

  displaySource(row: IContactLead): string {
    if (row.page_path) return row.page_path;
    if (!row.source_url) return '-';
    try {
      return new URL(row.source_url).pathname || row.source_url;
    } catch {
      return row.source_url;
    }
  }

  displayLocale(row: IContactLead): string {
    return `${row.locale || 'es'}`.toUpperCase();
  }

  get rangeLabel(): string {
    if (!this.total) return '0';
    const start = this.page * this.size + 1;
    const end = Math.min((this.page + 1) * this.size, this.total);
    return `${start}-${end}`;
  }

  private decorate(row: IContactLead): EditableContactLead {
    return {
      ...row,
      _draftStatus: row.status,
      _draftNotes: row.notes || '',
      _saving: false,
    };
  }

  private toast(severity: 'success' | 'error', detailKey: string) {
    this.messageService.add({
      severity,
      summary: this.translate.instant(
        severity === 'success' ? 'COMMON.SUCCESS' : 'COMMON.ERROR',
      ),
      detail: this.translate.instant(detailKey),
    });
  }
}
